import { memo, useCallback, useEffect, useRef } from 'react';
import { Box, Card, CardContent, useMediaQuery, useTheme } from '@mui/material';
import { loadGoogleMaps } from '../../../../utils/googleMapsLoader';
import { addStationMarker, drawLineBetweenStations } from '../utils/mapUtils';

import { MapContainerProps } from './MapContainer.types';

import './MapContainer.css';

export const MapContainer = memo(
  function MapContainer({
    selectedOkkoStation,
    selectedCompetitorStation,
    bindings,
    allCompetitorStations,
    okkoStations,
    deletedBindings,
    mapInitialized,
    setMapInitialized,
    showError
  }: MapContainerProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<any[]>([]);
    const polylinesRef = useRef<any[]>([]);
    const infoWindowRef = useRef<any>(null);
    const mapBoundsSetRef = useRef(false);
    const lastBindingsCountRef = useRef(0);

    useEffect(() => {
      if (mapInitialized) return;

      let isMounted = true;

      const initMap = async () => {
        try {
          await loadGoogleMaps();
          if (!isMounted || !mapRef.current) return;

          if (!window.google?.maps) return;

          mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 50.4501, lng: 30.5234 },
            zoom: 11,
            mapId: 'AZK_BINDING_MAP_ID',
            mapTypeId: window.google.maps.MapTypeId.ROADMAP
          });

          // Fix: Street View image doesn't update when pegman is dropped on a new location.
          // Force panorama to reload by toggling visibility on each position change.
          const panorama = mapInstanceRef.current.getStreetView();
          panorama.addListener('position_changed', () => {
            if (!panorama.getVisible()) return;
            const pos = panorama.getPosition();
            if (!pos) return;
            panorama.setVisible(false);
            panorama.setPosition(pos);
            panorama.setVisible(true);
          });

          if (isMounted) {
            setMapInitialized(true);
            const layout = document.querySelector('.azk-binding-main-layout');
            if (layout) {
              layout.classList.add('layout-ready');
            }
          }
        } catch (error: unknown) {
          console.error('[MapContainer] Initialization error:', error);
          if (isMounted) {
            const layout = document.querySelector('.azk-binding-main-layout');
            if (layout) {
              layout.classList.add('layout-ready');
            }
          }
        }
      };

      initMap();
      return () => {
        isMounted = false;
      };
    }, [mapInitialized, setMapInitialized]);

    const updateMapMarkers = useCallback(() => {
      if (!mapInstanceRef.current || !window.google) return;

      try {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }

        markersRef.current.forEach((marker: any) => {
          try {
            if (marker && marker.setMap) {
              marker.setMap(null);
            }
          } catch (e: unknown) {
            // Ignore cleanup errors
          }
        });
        polylinesRef.current.forEach((line: any) => {
          try {
            if (line && line.setMap) {
              line.setMap(null);
            }
          } catch (e: unknown) {
            // Ignore cleanup errors
          }
        });

        markersRef.current = [];
        polylinesRef.current = [];

        if (!selectedOkkoStation && !selectedCompetitorStation) return;

        const bounds = new window.google.maps.LatLngBounds();
        let shouldFitBounds = false;

        if (selectedOkkoStation) {
          const okkoMarker = addStationMarker(
            selectedOkkoStation,
            '#4caf50',
            bounds,
            mapInstanceRef.current,
            infoWindowRef,
            showError
          );
          if (okkoMarker) {
            markersRef.current.push(okkoMarker);
            shouldFitBounds = true;
          }

          bindings.forEach((binding: any) => {
            if (!deletedBindings.includes(binding.id)) {
              const competitor = allCompetitorStations.find(
                (s: any) => s.unicode === binding.competitor_station_unicode
              );
              if (competitor) {
                const compMarker = addStationMarker(
                  competitor,
                  '#ff9800',
                  bounds,
                  mapInstanceRef.current,
                  infoWindowRef,
                  showError
                );
                if (compMarker) markersRef.current.push(compMarker);

                const line = drawLineBetweenStations(selectedOkkoStation, competitor, mapInstanceRef.current);
                if (line) polylinesRef.current.push(line);
              }
            }
          });
        } else if (selectedCompetitorStation) {
          const compMarker = addStationMarker(
            selectedCompetitorStation,
            '#ff9800',
            bounds,
            mapInstanceRef.current,
            infoWindowRef,
            showError
          );
          if (compMarker) {
            markersRef.current.push(compMarker);
            shouldFitBounds = true;
          }

          bindings.forEach((binding: any) => {
            if (!deletedBindings.includes(binding.id)) {
              const okko = okkoStations.find((s: any) => s.unicode === binding.okko_station_unicode);
              if (okko) {
                const okkoMarker = addStationMarker(
                  okko,
                  '#4caf50',
                  bounds,
                  mapInstanceRef.current,
                  infoWindowRef,
                  showError
                );
                if (okkoMarker) markersRef.current.push(okkoMarker);

                const line = drawLineBetweenStations(okko, selectedCompetitorStation, mapInstanceRef.current);
                if (line) polylinesRef.current.push(line);
              }
            }
          });
        }

        const activeBindingsCount = bindings.filter((binding: any) => !deletedBindings.includes(binding.id)).length;
        const shouldAutoFit = !mapBoundsSetRef.current || activeBindingsCount !== lastBindingsCountRef.current;

        if (shouldAutoFit && shouldFitBounds && markersRef.current.length > 1) {
          mapInstanceRef.current.fitBounds(bounds);
          mapBoundsSetRef.current = true;
          lastBindingsCountRef.current = activeBindingsCount;
        } else if (shouldAutoFit && shouldFitBounds && markersRef.current.length === 1) {
          const position = markersRef.current[0].position || markersRef.current[0].getPosition();
          mapInstanceRef.current.setCenter(position);
          mapInstanceRef.current.setZoom(15);
          mapBoundsSetRef.current = true;
          lastBindingsCountRef.current = activeBindingsCount;
        }
      } catch (error: unknown) {
        console.error('Error updating map markers:', error);
      }
    }, [
      selectedOkkoStation,
      selectedCompetitorStation,
      bindings,
      allCompetitorStations,
      okkoStations,
      deletedBindings,
      showError
    ]);

    useEffect(() => {
      if (mapInstanceRef.current && (selectedOkkoStation || selectedCompetitorStation)) {
        // Exit Street View if active when station selection changes
        const panorama = mapInstanceRef.current.getStreetView();
        if (panorama.getVisible()) {
          panorama.setVisible(false);
        }
        mapBoundsSetRef.current = false;
        lastBindingsCountRef.current = 0;
        updateMapMarkers();
      }
    }, [selectedOkkoStation, selectedCompetitorStation, bindings, deletedBindings, updateMapMarkers]);

    return (
      <Box className={`map-container ${isMobile ? 'mobile-map' : ''}`}>
        <Card className="map-card">
          <CardContent className="map-content">
            <Box ref={mapRef} className="map-element" />
          </CardContent>
        </Card>
      </Box>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.selectedOkkoStation === nextProps.selectedOkkoStation &&
      prevProps.selectedCompetitorStation === nextProps.selectedCompetitorStation &&
      prevProps.bindings === nextProps.bindings &&
      prevProps.deletedBindings === nextProps.deletedBindings &&
      prevProps.mapInitialized === nextProps.mapInitialized
    );
  }
);
